import React, { useEffect, useState } from "react";
import { Pencil, ShieldCheck, ShieldAlert } from "lucide-react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import ProfileFormTenant from "../forms/ProfileFormTenant";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as authTenantActions from "src/redux/slices/authTenantSlice";

const EmailVerifyBadge: React.FC = () => {
  const dispatch = useAppDispatch();
  const { sendEmailOtp, verifyEmailOtp } = useAppSelector(
    (state) => state.authTenant
  );
  const [awaitingCode, setAwaitingCode] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (sendEmailOtp?.data && !sendEmailOtp?.error) {
      setAwaitingCode(true);
    } else if (sendEmailOtp?.error) {
      setError(sendEmailOtp.error);
    }
  }, [sendEmailOtp]);

  useEffect(() => {
    if (verifyEmailOtp?.data && !verifyEmailOtp?.error) {
      setAwaitingCode(false);
      setCode("");
    } else if (verifyEmailOtp?.error) {
      setError(verifyEmailOtp.error);
    }
  }, [verifyEmailOtp]);

  const handleSend = () => {
    setError("");
    dispatch(authTenantActions.sendEmailOtpRequest());
  };

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code");
      return;
    }
    dispatch(authTenantActions.verifyEmailOtpRequest({ code }));
  };

  if (awaitingCode) {
    return (
      <form onSubmit={handleVerify} className="mt-1.5 flex items-center gap-2">
        <Input
          value={code}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
            setError("");
          }}
          placeholder="123456"
          inputMode="numeric"
          maxLength={6}
          error={!!error}
          helperText={error || undefined}
          inputClassName="text-center tracking-[0.3em]"
          className="w-32"
        />
        <Button type="submit" size="sm" isLoading={verifyEmailOtp?.loading}>
          Confirm
        </Button>
        <button
          type="button"
          onClick={handleSend}
          disabled={sendEmailOtp?.loading}
          className="text-xs font-medium text-primary-600 hover:underline disabled:opacity-50 dark:text-primary-400"
        >
          Resend
        </button>
      </form>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSend}
      disabled={sendEmailOtp?.loading}
      className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50 dark:bg-amber-900/30 dark:text-amber-400"
    >
      <ShieldAlert size={12} />
      {sendEmailOtp?.loading ? "Sending code…" : "Not verified — click to verify"}
    </button>
  );
};

const ProfileCardTenant: React.FC = () => {
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (user) {
      setIsEditing(false);
    }
  }, [user]);

  if (isEditing) {
    return (
      <div className="space-y-3">
        <ProfileFormTenant handleCancel={() => setIsEditing(false)} />
      </div>
    );
  }

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");

  return (
    <div className="flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
      <div className="flex items-center gap-4">
        {user?.avatar ? (
          <img
            src={user.avatar}
            alt={fullName}
            className="h-16 w-16 rounded-full border border-gray-200 object-cover dark:border-gray-600"
          />
        ) : (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-primary-50 text-xl font-semibold text-primary-600 dark:border-gray-600 dark:bg-primary-900/40 dark:text-primary-300">
            {(user?.firstName || "U").charAt(0).toUpperCase()}
          </div>
        )}
        <div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {fullName || "-"}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {user?.email}
          </div>
          {user?.email &&
            (user?.emailVerified ? (
              <div className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                <ShieldCheck size={12} />
                Verified
              </div>
            ) : (
              <EmailVerifyBadge />
            ))}
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium capitalize text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
              {user?.roleDetails?.name || user?.role || "Role not assigned"}
            </span>
            {user?.tenantDetails && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {user.tenantDetails.name}
              </span>
            )}
          </div>
        </div>
      </div>

      <Button
        onClick={() => setIsEditing(true)}
        variant="outline"
        className="shrink-0"
      >
        <Pencil className="w-4 h-4 mr-1" /> Edit Profile
      </Button>
    </div>
  );
};

export default ProfileCardTenant;
